import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import AssetPhotoUpload from '../../components/assets/AssetPhotoUpload';
import InputModal from '../../components/modals/InputModal';
import Loader from '../../components/common/Loader';
import { useAssetForm } from '../../contexts/AssetFormContext';
import { PERMISSIONS, canFeatureForRole } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import '../../styles/Forms.css';

const AddAssetPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmployeeArea = location.pathname.startsWith('/employee');
  const assetsHomePath = isEmployeeArea ? '/employee/assets' : '/admin/assets';
  const {
    addFormData,
    addPhotoPreview,
    setAddFormField,
    setAddPhotoPreview,
    clearAddFormData,
  } = useAssetForm();

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  // const [photoFile, setPhotoFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [inputModal, setInputModal] = useState({ isOpen: false, type: null });
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }, []);
  const canAddAssets = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.ADD_ASSETS]),
    [normalizedUserRole, rolePermissions]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!canAddAssets) {
      toast.error('You do not have permission to add assets');
      navigate(assetsHomePath, { replace: true });
    }
  }, [permissionsReady, canAddAssets, navigate, assetsHomePath]);
  // Use formData from context
  const formData = addFormData;
  const photoPreview = addPhotoPreview;

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Refresh data when component becomes visible (user returns from navigation)
  useEffect(() => {
    const handleFocus = () => {
      fetchInitialData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      photoPreviews.forEach(preview => {
        if (preview.preview && preview.preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview.preview);
        }
      });
    };
  }, [photoPreviews]);

  const fetchInitialData = async () => {
    try {
      // Fetch sites
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      // Fetch departments
      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }

      // Fetch categories
      const categoriesRes = await api.getCategories({ isActive: 'true' });
      if (categoriesRes.success) {
        setCategories(categoriesRes.data || []);
      }

      // Fetch locations
      const locationsRes = await api.getLocations({ isActive: 'true' });
      if (locationsRes.success) {
        setLocations(locationsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddFormField(name, value);
    // Clear error for this field when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  // const handlePhotoChange = (file) => {
  //   if (file) {
  //     setPhotoFile(file);
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setAddPhotoPreview(reader.result);
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // };

  const handlePhotoChange = (filesOrUpdatedList) => {
    if (Array.isArray(filesOrUpdatedList)) {
      // Check if it's an array of File objects (new upload) or preview objects (removal)
      const isFileObjects = filesOrUpdatedList.length > 0 && filesOrUpdatedList[0] instanceof File;

      if (isFileObjects) {
        // New files uploaded - create preview objects
        const newPreviews = filesOrUpdatedList.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));
        // Combine with existing photos
        const combined = [...photoPreviews, ...newPreviews];
        setPhotoFiles(combined.map(p => p.file));
        setAddPhotoPreview(combined);
        setPhotoPreviews(combined);
      } else {
        // Updated list after removal - already has preview objects
        // Find removed photos and revoke their object URLs
        const removedPhotos = photoPreviews.filter(
          oldPreview => !filesOrUpdatedList.find(newPreview => newPreview.preview === oldPreview.preview)
        );
        removedPhotos.forEach(preview => {
          if (preview.preview && preview.preview.startsWith('blob:')) {
            URL.revokeObjectURL(preview.preview);
          }
        });

        setPhotoFiles(filesOrUpdatedList.map(p => p.file));
        setAddPhotoPreview(filesOrUpdatedList);
        setPhotoPreviews(filesOrUpdatedList);
      }
    }
  };

  const handleAddNew = async (type, value) => {
    try {
      if (type === 'site') {
        const response = await api.createSite({ name: value });
        if (response.success) {
          setSites([...sites, response.data]);
          setAddFormField('site', response.data.id || response.data._id);
        }
      } else if (type === 'department') {
        const response = await api.createDepartment({ name: value });
        if (response.success) {
          setDepartments([...departments, response.data]);
          setAddFormField('department', response.data.id || response.data._id);
        }
      } else if (type === 'category') {
        const response = await api.createCategory({ name: value, isActive: true });
        if (response.success) {
          setCategories([...categories, response.data]);
          setAddFormField('category', response.data.name);
          toast.success('Category created successfully');
        }
      } else if (type === 'location') {
        const response = await api.createLocation({ name: value, isActive: true });
        if (response.success) {
          setLocations([...locations, response.data]);
          setAddFormField('location', response.data.name);
          toast.success('Location created successfully');
        }
      }
    } catch (error) {
      console.error(`Error adding new ${type}:`, error);
      toast.error(`Error adding new ${type}. Please try again.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setFieldErrors({});

    // Client-side validation
    const errors = {};
    if (!formData.description) {
      errors.description = 'Description is required';
    }
    if (!formData.assetTag) {
      errors.assetTag = 'Asset Tag ID is required';
    }
    if (!formData.category) {
      errors.category = 'Category is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      setLoading(true);

      // Prepare asset data
      const assetData = {
        description: formData.description,
        assetTag: formData.assetTag.toUpperCase(),
        category: formData.category,
        brand: formData.brand,
        model: formData.model,
        serialNumber: formData.serialNumber,
        purchasedFrom: formData.purchasedFrom,
        cost: formData.cost ? parseFloat(formData.cost) : 0,
        purchaseDate: formData.purchaseDate || undefined,
        location: {
          site: formData.site || undefined,
          department: formData.department || undefined,
          locationDetail: formData.location || undefined,
        },
        category: formData.category || undefined,
      };

      // Create asset
      const response = await api.createAsset(assetData);

      if (response.success && photoFiles.length > 0) {
        // Upload photos to server
        try {
          const assetId = response.data._id || response.data.id;
          const uploadResponse = await api.uploadAssetPhoto(assetId, photoFiles);
          if (uploadResponse.success) {
            toast.success(`Asset created with ${uploadResponse.count || photoFiles.length} photo(s)!`);
          }
        } catch (error) {
          console.error('Error uploading photos:', error);
          toast.error(`Asset created but ${photoFiles.length > 1 ? 'some photos' : 'photo'} upload failed`);
        }
      }

      // Clear form data from context after successful submission
      clearAddFormData();
      // Clean up object URLs to prevent memory leaks
      photoPreviews.forEach(preview => {
        if (preview.preview && preview.preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview.preview);
        }
      });
      setPhotoFiles([]);
      setPhotoPreviews([]);
      setFieldErrors({});
      toast.success('Asset created successfully!');
      navigate(assetsHomePath);
    } catch (error) {
      console.error('Error creating asset:', error);
      
      // Parse error message and map to specific fields
      const errorMessage = error.message || 'Unknown error';
      const newErrors = {};

      // Check for assetTag duplicate error
      if (errorMessage.toLowerCase().includes('asset tag') || 
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('already in used')) {
        newErrors.assetTag = 'Asset Tag ID is already in used';
      } else if (errorMessage.toLowerCase().includes('description')) {
        newErrors.description = errorMessage;
      } else if (errorMessage.toLowerCase().includes('category')) {
        newErrors.category = errorMessage;
      } else {
        // For other errors, show as general error (could be shown at top of form)
        newErrors._general = errorMessage;
      }

      setFieldErrors(newErrors);
      
      // Only show toast for general errors, not field-specific ones
      if (newErrors._general) {
        toast.error('Error creating asset: ' + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!permissionsReady) {
    return <Loader />;
  }
  if (!canAddAssets) {
    return null;
  }

  return (
    <div className="add-asset-page" style={{ padding: '16px' }}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          {/* Left Column */}
          <div>
            {/* Asset Details Section */}
            <div style={{
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>
                Asset Details
              </h2>

              <div className="form-group">
                <label htmlFor="description">
                  Description <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <input
                  id="description"
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter the full name of the asset"
                  required
                  style={{
                    borderColor: fieldErrors.description ? '#e53e3e' : undefined
                  }}
                />
                {fieldErrors.description && (
                  <div style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                    {fieldErrors.description}
                  </div>
                )}
                <small style={{ color: '#718096', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  Example: "Blackmagic ATEM Mini PRO switchers", "Z CAM E2 4K Camera"
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="assetTag">
                  Asset Tag ID <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <input
                  id="assetTag"
                  type="text"
                  name="assetTag"
                  value={formData.assetTag}
                  onChange={handleInputChange}
                  placeholder="A0001, I0056, CAM0042"
                  required
                  style={{
                    textTransform: 'uppercase',
                    borderColor: fieldErrors.assetTag ? '#e53e3e' : undefined
                  }}
                />
                {fieldErrors.assetTag && (
                  <div style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                    {fieldErrors.assetTag}
                  </div>
                )}
                <small style={{ color: '#718096', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  Unique ID assigned to the asset. Used on labels, QR codes, and in lists.
                </small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label htmlFor="purchaseDate">Purchase Date</label>
                  <input
                    id="purchaseDate"
                    type="date"
                    name="purchaseDate"
                    value={formData.purchaseDate}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cost">Cost (₹)</label>
                  <input
                    id="cost"
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="purchasedFrom">Purchased From</label>
                <input
                  id="purchasedFrom"
                  type="text"
                  name="purchasedFrom"
                  value={formData.purchasedFrom}
                  onChange={handleInputChange}
                  placeholder="Amazon, B&H Photo, Netgear Dealer"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label htmlFor="brand">Brand</label>
                  <input
                    id="brand"
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    placeholder="Blackmagic, Z CAM, Netgear"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="model">Model</label>
                  <input
                    id="model"
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="ATEM Mini PRO, E2 4K, SFP 1G"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="serialNumber">Serial Number</label>
                <input
                  id="serialNumber"
                  type="text"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleInputChange}
                  placeholder="Manufacturer's serial number"
                />
                <small style={{ color: '#718096', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  Useful for warranty, repair, and tracking
                </small>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div>
            {/* Site, Location, Category, Department Section */}
            <div style={{
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>
                Site, Location, Category and Department
              </h2>

              <div className="form-group">
                <label htmlFor="site">Site</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="site"
                    name="site"
                    value={formData.site}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Site</option>
                    {sites.map(site => (
                      <option key={site.id || site._id} value={site.id || site._id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/sites/add')}
                    style={{
                      padding: '10px 12px',
                      background: '#f7fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + New
                  </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="category">
                  Category <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    style={{
                      flex: 1,
                      borderColor: fieldErrors.category ? '#e53e3e' : undefined
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id || cat._id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/categories/add')}
                    style={{
                      padding: '10px 12px',
                      background: '#f7fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + New
                  </button>
                  )}
                </div>
                {fieldErrors.category && (
                  <div style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                    {fieldErrors.category}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Location</option>
                    {locations.map(loc => (
                      <option key={loc.id || loc._id} value={loc.name}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/locations/add')}
                    style={{
                      padding: '10px 12px',
                      background: '#f7fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + New
                  </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="department">Department</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id || dept._id} value={dept.id || dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/departments/add')}
                    style={{
                      padding: '10px 12px',
                      background: '#f7fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + New
                  </button>
                  )}
                </div>
              </div>
            </div>

            {/* Asset Photo Section */}
            <div style={{
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>
                Asset Photo
              </h2>
              <AssetPhotoUpload
                onUpload={handlePhotoChange}
                currentPhotos={photoPreviews}
              />
              <small style={{ color: '#718096', fontSize: '11px', marginTop: '8px', display: 'block' }}>
                Allowed formats: JPG, GIF, PNG. This photo will appear in the List of Assets and the Asset View page.
              </small>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid #e2e8f0'
        }}>
          <button
            type="button"
            onClick={() => navigate(assetsHomePath)}
            style={{
              padding: '10px 20px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </form>

      {/* Input Modal */}
      <InputModal
        isOpen={inputModal.isOpen}
        onClose={() => setInputModal({ isOpen: false, type: null })}
        onSubmit={(value) => {
          if (inputModal.type) {
            handleAddNew(inputModal.type, value);
          }
        }}
        title={
          inputModal.type === 'site' ? 'Add New Site' :
            inputModal.type === 'location' ? 'Add New Location' :
              inputModal.type === 'category' ? 'Add New Category' :
                inputModal.type === 'department' ? 'Add New Department' :
                  'Add New'
        }
        label={
          inputModal.type === 'site' ? 'Site Name' :
            inputModal.type === 'location' ? 'Location' :
              inputModal.type === 'category' ? 'Category' :
                inputModal.type === 'department' ? 'Department Name' :
                  'Name'
        }
        placeholder={
          inputModal.type === 'site' ? 'Enter new site name' :
            inputModal.type === 'location' ? 'Enter new location' :
              inputModal.type === 'category' ? 'Enter new category' :
                inputModal.type === 'department' ? 'Enter new department name' :
                  'Enter name'
        }
      />
    </div>
  );
};

export default AddAssetPage;

